/**
 * Safety Management workspace customer-facing presentation copy (display only).
 *
 * Workspace job: what safety problems are actively managed, which need attention,
 * what measurement supports them, and what records have been produced.
 *
 * Left nav stays "Safety Cases". Page title is Safety Management.
 * Case = Safety Case. Safety Scan = measurement window.
 * Routes, API keys, and lifecycle enums are unchanged.
 */

export const WORKSPACE_PAGE_TITLE = 'Safety Management';
export const WORKSPACE_PAGE_SUBTITLE =
  'Manage recurring safety issues, measurement cycles, controls, and signed evidence.';

export const WORKSPACE_TAB_LABELS = {
  attention: 'Action required',
  cases: 'Safety Cases',
  scans: 'Safety Scans',
  records: 'Signed Records',
} as const;

export const WORKSPACE_TAB_HELPERS = {
  attention: 'Items requiring review or action.',
  cases: 'Recurring safety conditions being managed.',
  scans: 'Measurement history for Safety Cases.',
  records: 'Frozen measurement records preserved after review and sign-off.',
} as const;

export const OPEN_SAFETY_CASE_LABEL = 'Open Safety Case';
export const VIEW_SAFETY_SCAN_LABEL = 'View Safety Scan';
export const SIGN_SAFETY_SCAN_LABEL = 'Sign Safety Scan';
export const REVIEW_AND_SIGN_LABEL = 'Review & sign';
export const OPEN_SCAN_LABEL = 'Open Scan';
export const VIEW_RECORD_LABEL = 'View Signed Record';
export const CREATE_CASE_LABEL = 'Create Case';
export const START_MONITORING_LABEL = 'Start monitoring';
export const REVIEW_NEW_ACTIVITY_LABEL = 'Review new activity';
export const VIEW_HISTORY_LABEL = 'View history';

export const ATTENTION_EMPTY = 'Nothing needs action right now.';
export const ATTENTION_HELPER = WORKSPACE_TAB_HELPERS.attention;

export const SAFETY_CASES_EMPTY = 'No Safety Cases yet.';
export const SAFETY_CASES_EMPTY_HINT = 'Recurring patterns can be reviewed in Investigate.';
export const SAFETY_SCANS_EMPTY = 'No Safety Scans in this scope.';
export const SAFETY_SCANS_CURRENT_EMPTY = 'No current or upcoming Safety Scan.';
export const NO_COMPLETED_SAFETY_SCAN_YET = 'No completed Safety Scan yet';
export const RECORDS_EMPTY =
  'No signed Safety Scan records yet. Records appear after a Safety Scan is signed.';
export const RECORDS_SIGNING_NOTE =
  'Signing a Safety Scan does not close the Safety Case.';

export const SAFETY_CASES_SUBTITLE = WORKSPACE_TAB_HELPERS.cases;
export const SAFETY_SCANS_SUBTITLE = WORKSPACE_TAB_HELPERS.scans;
export const RECORDS_SUBTITLE = WORKSPACE_TAB_HELPERS.records;

export const SAFETY_CASES_PORTFOLIO_EMPTY =
  'Org portfolio mode does not include a cross-site Safety Cases register yet. Switch to a site or use Attention for portfolio items with site context.';
export const SAFETY_SCANS_PORTFOLIO_EMPTY =
  'Org portfolio mode does not include a cross-site Safety Scans register yet. Switch to a site to review measurement windows.';
export const RECORDS_PORTFOLIO_EMPTY =
  'Org portfolio mode does not include a cross-site Signed Records register yet. Switch to a site to review frozen signed Scan results.';

export const CASE_COL_ISSUE = 'Issue';
export const CASE_COL_LOCATION = 'Location';
export const CASE_COL_OWNER = 'Owner';
export const CASE_COL_STATUS = 'Status';
export const CASE_COL_CURRENT_SCAN = 'Current measurement';
export const CASE_COL_LATEST_SCAN = 'Latest Signed';
export const CASE_COL_SCANS = 'Scans';
export const CASE_COL_CONTROL = 'Control';
export const CASE_COL_NEXT_STEP = 'Next step';

export const SCAN_COL_SAFETY_CASE = 'Safety Case';
export const SCAN_COL_SCAN = 'Safety Scan';
export const SCAN_COL_WINDOW = 'Window';
export const SCAN_COL_RESULT = 'Result';
export const SCAN_COL_VERIFICATION = 'Verification';
export const SCAN_COL_COVERAGE = 'Coverage';
export const SCAN_COL_STATUS = 'Status';

export const LIFECYCLE_LOOP_NOTE =
  'Action required is the work queue, not a lifecycle step. New activity may reopen the Safety Case.';
export const STATUS_LEGEND_LINE = 'Work state · Status';
export const STATUS_DIMENSION_CONTROL_STAGE = 'Status';

export const OPEN_NEXT_SAFETY_CASE_WITH_NEW_ACTIVITY =
  'Open next Safety Case with new activity';
export const MANAGE_RECURRING_SAFETY_SCANS = 'Manage recurring Safety Scans';

export const AD_HOC_NO_RELATED_SAFETY_CASE = 'Ad hoc / No related Safety Case';
export const MANAGED_CASE_NOT_CREATED = 'Managed Case: Not created';

export const SCAN_OCCURRENCE_LABEL = 'Occurrence';
export const FOLLOW_UP_MEASUREMENT_LABEL = 'Follow-up measurement';

export function hazardCategoryCoverageCopy(classified: number, total: number): string {
  return `${classified} of ${total} Safety Cases categorized`;
}

export function caseRegisterNextStepDisplay(nextStep: string, actionLabel: string): string | null {
  const next = String(nextStep || '').trim();
  const action = String(actionLabel || '').trim();
  if (!next) return null;
  if (next.toLowerCase() === action.toLowerCase()) return null;
  return next;
}

export function caseScanCountDisplay(label: string): string {
  const text = String(label || '').trim();
  if (!text || text === '—') return '—';
  if (/\bscans?\b/i.test(text)) return text;
  return `${text} scans`;
}

export function safetyScansCurrentEmptyCopy(nextScheduled: string | null | undefined): string {
  const next = String(nextScheduled || '').trim();
  if (!next) return SAFETY_SCANS_CURRENT_EMPTY;
  return `${SAFETY_SCANS_CURRENT_EMPTY} Next scheduled: ${next}.`;
}

/**
 * Baseline / follow-up labels only from persisted lifecycle signals.
 * Scheduled/Manual/unknown never become "Baseline measurement".
 */
export function scanLifecycleMeasurementLabel(trigger: string | null | undefined): string | null {
  const normalized = String(trigger || '').trim().toLowerCase();
  if (normalized === 'follow-up') return FOLLOW_UP_MEASUREMENT_LABEL;
  return null;
}

export function managedCaseLabel(caseTitle: string | null | undefined, caseId: string | null | undefined): string {
  if (String(caseId || '').trim()) {
    return `Managed Case: ${String(caseTitle || '').trim() || '—'}`;
  }
  return MANAGED_CASE_NOT_CREATED;
}

export const ATTENTION_METRIC_LABELS = {
  needs_review: 'Patterns needing review',
  active_cases: 'Active Safety Cases',
  awaiting_signoff: 'Safety Scans to sign',
  new_activity: 'New activity',
  coverage_run: 'Coverage issues',
  overdue: 'Overdue',
  unowned: 'Unowned',
} as const;

const ATTENTION_OBJECT_LABELS = {
  risk_pattern_review: 'Pattern',
  active_safety_case: 'Safety Case',
  awaiting_signoff: 'Safety Scan',
  new_activity: 'Safety Case',
  coverage_run: 'Safety Scan',
} as const;

export function attentionObjectLabel(itemType: keyof typeof ATTENTION_OBJECT_LABELS): string {
  return ATTENTION_OBJECT_LABELS[itemType];
}

export function registerEmptyNoun(kind: 'cases' | 'scans' | 'records'): string {
  if (kind === 'cases') return 'Safety Cases';
  if (kind === 'scans') return 'Safety Scans';
  return 'Signed Records';
}
